import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SubtitleService } from 'src/app/services/subtitle.service';
import { UserService } from 'src/app/services/user.service';
import { RoutingService } from 'src/app/services/routing.service';
import { HttpService } from 'src/app/services/http.service';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { ConfirmationService } from 'primeng/api';
import {
  AmazonPriceTracksGQL,
  AmazonPriceTracksQuery,
  UpsertAmazonPriceTrackGQL,
  DeleteAmazonPriceTrackGQL,
  CheckSpapiTokenValidGQL,
} from 'src/gql/generated';
import { MessageService } from 'src/app/services/message.service';
import { firstValueFrom } from 'rxjs';
import { gqlErrors } from 'src/app/utils/gql-errors';

type PriceTrack = NonNullable<NonNullable<AmazonPriceTracksQuery['amazonPriceTrack']>[number]>;

@Component({
  selector: 'app-amazon-price-tracking',
  templateUrl: './amazon-price-tracking.component.html',
  styleUrls: ['./amazon-price-tracking.component.sass'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    ProgressSpinnerModule,
    MessageModule,
    TableModule,
    InputTextModule,
    InputNumberModule,
    ConfirmDialogModule,
    DialogModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService],
})
export class AmazonPriceTrackingComponent implements OnInit, OnDestroy {
  isLoading = true;
  isRegisteredTelegram = false;
  telegramToken: string;
  telegramName: string;
  isPolling = false;
  isTimeout = false;

  // SP-API トークン検証
  isSpapiTokenValid = true;
  spapiTokenMessage: string;

  // ダイアログ
  showAddDialog = false;

  // CRUD
  priceTracks: PriceTrack[] = [];
  isLoadingTracks = false;
  newAsin = '';
  newThresholdPrice: number | null = null;
  isSubmitting = false;

  // 編集
  editingTrackId: number | null = null;
  editThresholdPrice: number | null = null;

  private pollingTimer: ReturnType<typeof setInterval> | null = null;
  private timeoutTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly MAX_TRACKS = 10;
  private readonly POLLING_INTERVAL = 5000;
  private readonly POLLING_TIMEOUT = 180000;

  constructor(
    private subtitleService: SubtitleService,
    private userService: UserService,
    private routingService: RoutingService,
    private http: HttpService,
    private confirmationService: ConfirmationService,
    private amazonPriceTracksGQL: AmazonPriceTracksGQL,
    private upsertAmazonPriceTrackGQL: UpsertAmazonPriceTrackGQL,
    private deleteAmazonPriceTrackGQL: DeleteAmazonPriceTrackGQL,
    private checkSpapiTokenValidGQL: CheckSpapiTokenValidGQL,
    private messageService: MessageService,
  ) {
    this.routingService.loginRequired();
    this.userService.amazonPriceTrackingRequired();
    this.subtitleService.setSubTitle('Amazon価格追跡');
  }

  ngOnInit() {
    this.loadTelegram();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  async loadTelegram() {
    this.isLoading = true;
    await this.userService.getUserTelegram((resp) => {
      this.telegramToken = resp.me.telegramToken;
      const userTelegram = resp.me.userTelegram;
      if (userTelegram && userTelegram.chatID) {
        this.isRegisteredTelegram = true;
        this.telegramName = userTelegram.lastName + ' ' + userTelegram.firstName;
        this.checkSpapiToken();
      }
      this.isLoading = false;
    });
  }

  async checkSpapiToken() {
    const resp = await firstValueFrom(this.checkSpapiTokenValidGQL.fetch({ fetchPolicy: 'network-only' }));
    if (gqlErrors(resp)) {
      this.http.handleError();
      return;
    }
    this.isSpapiTokenValid = resp.data.checkSpapiTokenValid.valid;
    this.spapiTokenMessage = resp.data.checkSpapiTokenValid.message;
    if (this.isSpapiTokenValid) {
      this.loadPriceTracks();
    }
  }

  getSpapiReconnectLink(): string {
    return 'https://sellercentral.amazon.co.jp/apps/authorize/consent?application_id=amzn1.sellerapps.app.2061b3cc-fc3b-48cc-8b52-fd333bc47f68&state=link';
  }

  getTelegramLink(): string {
    return `https://t.me/RakujanBot?start=${this.telegramToken}`;
  }

  onClickTelegramLink() {
    this.isPolling = true;
    this.isTimeout = false;
    this.startPolling();
  }

  // --- CRUD ---

  async loadPriceTracks() {
    this.isLoadingTracks = true;
    const resp = await firstValueFrom(this.amazonPriceTracksGQL.fetch({ fetchPolicy: 'network-only' }));
    if (gqlErrors(resp)) {
      this.http.handleError();
    } else {
      this.priceTracks = (resp.data.amazonPriceTrack || []).filter((t) => t != null) as PriceTrack[];
    }
    this.isLoadingTracks = false;
  }

  get isMaxTracksReached(): boolean {
    return this.priceTracks.length >= this.MAX_TRACKS;
  }

  async onAddTrack() {
    if (this.isMaxTracksReached) {
      return;
    }
    const asin = this.extractAsin(this.newAsin.trim());
    if (!asin || this.newThresholdPrice == null || this.newThresholdPrice <= 0) {
      return;
    }
    this.isSubmitting = true;
    const resp = await firstValueFrom(
      this.upsertAmazonPriceTrackGQL.mutate({ variables: { asin, thresholdPrice: this.newThresholdPrice } }),
    );
    if (gqlErrors(resp)) {
      const error = gqlErrors(resp)[0];
      if (error.extensions?.['code'] === 'E0016') {
        this.messageService.setFlashMessageWithClear('error', 'エラー', error.message);
        this.showAddDialog = false;
        await this.loadPriceTracks();
      } else {
        this.http.handleError();
      }
    } else {
      this.newAsin = '';
      this.newThresholdPrice = null;
      this.showAddDialog = false;
      await this.loadPriceTracks();
    }
    this.isSubmitting = false;
  }

  onEditTrack(track: PriceTrack) {
    this.editingTrackId = track.id;
    this.editThresholdPrice = track.thresholdPrice;
  }

  onCancelEdit() {
    this.editingTrackId = null;
    this.editThresholdPrice = null;
  }

  async onSaveEdit(track: PriceTrack) {
    if (this.editThresholdPrice == null || this.editThresholdPrice <= 0) {
      return;
    }
    this.isSubmitting = true;
    const resp = await firstValueFrom(
      this.upsertAmazonPriceTrackGQL.mutate({
        variables: {
          asin: track.amazonProduct.asin,
          thresholdPrice: this.editThresholdPrice,
        },
      }),
    );
    if (gqlErrors(resp)) {
      this.http.handleError();
    } else {
      this.editingTrackId = null;
      this.editThresholdPrice = null;
      await this.loadPriceTracks();
    }
    this.isSubmitting = false;
  }

  onDeleteTrack(track: PriceTrack) {
    this.confirmationService.confirm({
      message: `${track.amazonProduct.name} の追跡を削除しますか？`,
      header: '削除確認',
      acceptLabel: '削除',
      rejectLabel: 'キャンセル',
      accept: async () => {
        const resp = await firstValueFrom(this.deleteAmazonPriceTrackGQL.mutate({ variables: { id: track.id } }));
        if (gqlErrors(resp)) {
          this.http.handleError();
        } else {
          await this.loadPriceTracks();
        }
      },
    });
  }

  extractAsin(input: string): string {
    const asinMatch = input.match(/^[A-Z0-9]{10}$/);
    return asinMatch ? input : '';
  }

  // --- Polling ---

  private startPolling() {
    this.pollingTimer = setInterval(() => {
      this.checkTelegramStatus();
    }, this.POLLING_INTERVAL);

    this.timeoutTimer = setTimeout(() => {
      this.stopPolling();
      this.isPolling = false;
      this.isTimeout = true;
    }, this.POLLING_TIMEOUT);
  }

  private stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  private async checkTelegramStatus() {
    await this.userService.getUserTelegram((resp) => {
      const userTelegram = resp.me.userTelegram;
      if (userTelegram && userTelegram.chatID) {
        this.stopPolling();
        this.isPolling = false;
        this.isRegisteredTelegram = true;
        this.telegramName = userTelegram.lastName + ' ' + userTelegram.firstName;
        this.checkSpapiToken();
      }
    });
  }

  onRetry() {
    this.isTimeout = false;
  }
}
